import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, 
  Filter, 
  Search, 
  ChevronLeft, 
  ChevronRight,
  X,
  SlidersHorizontal,
  ChevronDown
} from 'lucide-react';

const Container = styled.div`
  min-height: 100vh;
  background: #0f0f0f;
  color: #ffffff;
`;

const Header = styled.header`
  background: #1a1a1a;
  padding: 1.5rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const HeaderContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  
  @media (max-width: 768px) {
    padding: 0 1rem;
    flex-direction: column;
    align-items: flex-start;
  }
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const HeaderTitle = styled.div`
  h1 {
    font-size: 30px;
    font-weight: 500;
    margin: 0;
    color: #ffffff;
    
    @media (max-width: 768px) {
      font-size: 22px;
    }
  }
  
  p {
    font-size: 1rem;
    color: rgba(255, 255, 255, 0.7);
    margin: 0.25rem 0 0 0;
  }
`;

const MainContent = styled.main`
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const DealerInfoSection = styled.section`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const DealerInfoHeader = styled.div`
  margin-bottom: 1rem;
  
  h3 {

    font-size: 18px;
    font-weight: 500;
    margin: 0;
    color: #F43F60;
  }
`;

const DealerInfoContent = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  
  div {
    color: rgba(255, 255, 255, 0.8);
    font-size: 16px;
    
    strong {
      color: #ffffff;
      margin-right: 0.5rem;
    }
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FiltersSection = styled.section`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 20px;
  margin-bottom: 2rem;
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  overflow: hidden;
`;

const FiltersHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border-bottom: 1px solid rgba(244, 63, 96, 0.2);
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.02);
  }
  
  h3 {
    font-size: 20px;
    font-weight: 600;
    margin: 0;
    color: #F43F60;
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
`;

const FiltersToggle = styled.button`
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  font-size: 0.9rem;
  
  &:hover {
    background: rgba(244, 63, 96, 0.1);
    transform: scale(1.05);
  }
  
  svg {
    transition: transform 0.3s ease;
  }
  
  &.expanded svg {
    transform: rotate(180deg);
  }
`;

const FiltersContent = styled.div`
  padding: 2rem;
  transition: all 0.3s ease;
  max-height: ${props => props.expanded ? '2000px' : '0'};
  opacity: ${props => props.expanded ? '1' : '0'};
  overflow: hidden;
`;

const ClearFiltersButton = styled.button`
  background: rgba(244, 63, 96, 0.15);
  border: 2px solid rgba(244, 63, 96, 0.3);
  color: #fff;
  padding: 8px 15px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background: rgba(244, 63, 96, 0.25);
    border-color: rgba(244, 63, 96, 0.5);
  }
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  position: relative;
  
  &:hover {
    transition: transform 0.2s ease;
  }
`;

const FilterLabel = styled.label`
  font-size: 16px;
  font-weight: 500;
  line-height: 1.3;
  color: rgba(255, 255, 255, 0.9);
  text-transform: capitalize;
  letter-spacing: 0.5px;
  margin-bottom: 0.25rem;
`;

const FilterInput = styled.input`
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  padding: 8px 15px;
  color: #ffffff;
  font-size: 16px;
  outline: none;
  transition: all 0.3s ease;
  font-weight: 500;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
    font-weight: 400;
  }
  
  &:focus {
    border-color: #F43F60;
    background: rgba(255, 255, 255, 0.05);
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`;

const FilterSelect = styled.select`
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  padding: 8px 15px;
  color: #ffffff;
  font-size: 16px;
  outline: none;
  transition: all 0.3s ease;
  cursor: pointer;
  font-weight: 500;
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 1.5em;
  padding-right: 3rem;
  
  &:focus {
    border-color: #F43F60;
    background-color: rgba(255, 255, 255, 0.05);
    box-shadow: 0 0 0 4px rgba(244, 63, 96, 0.1);
  }
  
  &:hover {
    border-color: rgba(255, 255, 255, 0.4);
    background-color: rgba(255, 255, 255, 0.05);
  }
  
  option {
    background: #1a1a1a;
    color: #ffffff;
    padding: 0.5rem;
    &:hover { 
      background: #F43F60;
      color: #ffffff;
    }
    &:focus { 
      background: #F43F60;
      color: #ffffff;
    }
    &:checked { 
      background: #F43F60;
      color: #ffffff;
    }
  }
`;

const SearchButton = styled.button`
  background: linear-gradient(135deg, #F43F60 0%, #FF6B6B 100%);
  border: none;
  color: white;
  padding: 12px 20px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 700;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  box-shadow: 0 4px 20px rgba(244, 63, 96, 0.3);
  text-transform: capitalize;
  letter-spacing: 0.5px;
  
  &:hover {
    box-shadow: 0 8px 30px rgba(244, 63, 96, 0.4);
    background: linear-gradient(135deg, #FF6B6B 0%, #F43F60 100%);
  }

`;

const FiltersActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const FilterChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const FilterChip = styled.span`
  background: #1a1a1a;
  color: #fff;
  color: #F43F60;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  .remove {
    cursor: pointer;
    opacity: 0.7;
    transition: opacity 0.2s ease;
    
    &:hover {
      opacity: 1;
    }
  }
`;

const ResultsSection = styled.section`
  margin-bottom: 2rem;
`;

const ResultsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  
  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    margin: 0;
    color: #F43F60;
  }
  
  .results-count {
    color: rgba(255, 255, 255, 0.7);
    font-size: 18px;
  }
`;

const CarsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CarCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
    border-color: rgba(244, 63, 96, 0.3);
    
    img {
      transform: scale(1.05);
    }
  }
`;

const CarImage = styled.div`
  position: relative;
  height: 200px;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
`;

const CarInfo = styled.div`
  padding: 15px;
`;

const CarTitle = styled.h4`
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 .85rem 0;
  color: #ffffff;
`;

const CarDetails = styled.div`
  display: flex;
  align-items: self-start;
  flex-wrap: wrap;
  margin-bottom: 10px;
  min-height: 78px;
  
  span {
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.8);
    padding: 0.25rem 0.5rem;
    border-radius: 8px;
    font-size: 14px;
     display: inline-block;
    margin-right: 0.5rem;
    margin-bottom: 0.5rem;
    height: 29px;
  }
`;

const CarPrice = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #F43F60;
  margin-bottom: 1rem;
`;

const CarActions = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const ActionButton = styled.button`
  flex: 1;
  background: ${props => props.primary ? 'linear-gradient(45deg, #F43F60, #FF6B6B)' : 'rgba(255, 255, 255, 0.1)'};
  border: 1px solid ${props => props.primary ? 'transparent' : 'rgba(255, 255, 255, 0.2)'};
  color: white;
  padding: 8px 2px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 2rem;
`;

const PageButton = styled.button`
  background: ${props => props.active ? 'linear-gradient(45deg, #F43F60, #FF6B6B)' : 'rgba(255, 255, 255, 0.1)'};
  border: 1px solid ${props => props.active ? 'transparent' : 'rgba(255, 255, 255, 0.2)'};
  color: white;
  padding: 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
  font-size: 16px;
  line-height: 1;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageEllipsis = styled.span`
  color: rgba(255, 255, 255, 0.5);
  padding: 0.75rem 0.5rem;
  font-size: 16px;
  user-select: none;
`;

const PageJumpContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: 1rem;
  
  span {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.9rem;
  }
`;

const PageJumpInput = styled.input`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  padding: 6px;
  color: #ffffff;
  font-size: 16px;
  width: 70px;
  text-align: center;
  outline: none;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  &:focus {
    border-color: #F43F60;
  }
`;

const PageJumpButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 6px 10px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 16px;
  line-height: 1.3;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50vh;
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.7);
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  text-align: center;
  gap: 1rem;
  
  h2 {
    color: #F43F60;
    margin: 0;
  }
  
  p {
    color: rgba(255, 255, 255, 0.7);
    margin: 0;
  }
`;

const NoResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  text-align: center;
  gap: 1rem;
  
  h3 {
    color: rgba(255, 255, 255, 0.7);
    margin: 0;
  }
  
  p {
    color: rgba(255, 255, 255, 0.5);
    margin: 0;
  }
`;

const DealerInventory = () => {
  const { dealerId } = useParams();
  const navigate = useNavigate();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dealer, setDealer] = useState(null);
  const [totalCars, setTotalCars] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    make: '',
    model: '',
    year: '',
    minPrice: '',
    maxPrice: '',
    newUsed: '',
    transmission: '',
    drivetrain: ''
  });
  const [pageJumpInput, setPageJumpInput] = useState('');
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  const limit = 12; // Cars per page

  useEffect(() => {
    fetchInventory();
  }, [dealerId, currentPage]);

  const fetchInventory = async (clearFilters = false) => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: currentPage,
        limit,
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== '' && !clearFilters)
        )
      });

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/inventory/search-by-dealer/?dealerId=${dealerId}&${queryParams}`,
        {
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('token')}`
          }
        }
      );

      const data = await response.json();

      if (data.status === 'success') {
        setCars(data.data.inventory || []);
        setDealer(data.data.dealer || null);
        setTotalCars(data.data.pagination?.total || 0);
        setTotalPages(data.data.pagination?.pages || 1);
      } else {
        throw new Error(data.message || 'Failed to fetch inventory');
      }
    } catch (err) {
      console.error('Error fetching inventory:', err);
      setError('Failed to load dealer inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const applyFilters = () => {
    setCurrentPage(1); // Reset to first page when filters are applied
    fetchInventory();
  };

  const clearFilters = () => {
    setFilters({
      make: '',
      model: '',
      year: '',
      minPrice: '',
      maxPrice: '',
      newUsed: '',
      transmission: '',
      drivetrain: ''
    });
    setCurrentPage(1);
    // Fetch inventory with cleared filters
    fetchInventory(true)
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageJump = () => {
    const page = parseInt(pageJumpInput);
    if (page && page >= 1 && page <= totalPages) {
      handlePageChange(page);
      setPageJumpInput('');
    }
  };

  const handlePageJumpKeyPress = (e) => {
    if (e.key === 'Enter') {
      handlePageJump();
    }
  };

  const handleCarClick = (car) => {
    navigate(`/car-details?id=${car.id}`);
  };

  const handleChatAboutCar = (car) => {
    // Set the selected car in session storage with full details
    let images = [];
    try {
      // Try parsing as JSON first
      try {
        images = JSON.parse(car.imagelist);
      } catch {
        // If JSON parse fails, treat as CSV
        images = car.imagelist.split(',').map(url => ({ url: url.trim() }));
      }
    } catch (err) {
      console.error('Error parsing imagelist:', err);
      images = [];
    }

    sessionStorage.setItem('selectedCar', JSON.stringify({
      id: car.id,
      dealer_id: car.dealer_id,
      make: car.make,
      model: car.model,
      year: car.year,
      price: car.price,
      mileage: car.mileage,
      transmission: car.transmission,
      drivetrain: car.drivetrain,
      trim: car.trim,
      body_style: car.body_style,
      ext_color: car.ext_color,
      int_color: car.int_color,
      engine: car.engine,
      fuel: car.fuel,
      cylinders: car.cylinders,
      doors: car.doors,
      vin: car.vin,
      stock_no: car.stock_no,
      certified: car.certified,
      description: car.description,
      imagelist: images,
      images: images,
      dealer_phone_number: car.dealer_phone_number,
      city: car.city,
      status: car.status,
      new_used: car.new_used,
      msrp: car.msrp,
      vehicle_information: car.vehicle_information
    }));
   
    // Navigate to the chat page
    navigate('/');
  };

  const handleBack = () => {
    navigate(-1);
  };

  const toggleFilters = () => {
    setFiltersExpanded(!filtersExpanded);
  };

  if (loading) {
    return (
      <Container>
        <LoadingContainer>Loading dealer inventory...</LoadingContainer>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorContainer>
          <h2>Error</h2>
          <p>{error}</p>
        </ErrorContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <HeaderContent>
          <BackButton onClick={handleBack}>
            <ArrowLeft size={16} />
            Back
          </BackButton>
          <HeaderTitle>
            <h1>{dealer ? `${dealer.name} Inventory` : 'Dealer Inventory'}</h1>
            <p>{dealer ? `Browse all available vehicles from ${dealer.name} in ${dealer.city}, ${dealer.state}` : 'Browse all available vehicles from this dealer'}</p>
          </HeaderTitle>
        </HeaderContent>
      </Header>

      <MainContent>
        {dealer && (
          <DealerInfoSection>
            <DealerInfoHeader>
              <h3>Dealer Information</h3>
            </DealerInfoHeader>
            <DealerInfoContent>
              <div>
                <strong>Name:</strong> {dealer.name}
              </div>
              <div>
                <strong>Phone:</strong> {dealer.phone}
              </div>
              <div>
                <strong>Location:</strong> {dealer.city}, {dealer.state} {dealer.postalCode}
              </div>
              {dealer.email && (
                <div>
                  <strong>Email:</strong> {dealer.email}
                </div>
              )}
            </DealerInfoContent>
          </DealerInfoSection>
        )}
        <FiltersSection>
          <FiltersHeader>
            <div onClick={toggleFilters} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <h3>
                <Filter size={24} />
                Advanced Filters
              </h3>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <ClearFiltersButton onClick={clearFilters}>
                <X size={16} />
                Clear All
              </ClearFiltersButton>
              <FiltersToggle className={filtersExpanded ? 'expanded' : ''} onClick={toggleFilters}>
                {filtersExpanded ? 'Collapse' : 'Expand'}
                <ChevronDown size={20} />
              </FiltersToggle>
            </div>
          </FiltersHeader>

          <FiltersContent expanded={filtersExpanded}>
            <FiltersGrid>
              <FilterGroup>
                <FilterLabel>Make</FilterLabel>
                <FilterInput
                  type="text"
                  placeholder="e.g., Toyota, Ford, BMW"
                  value={filters.make}
                  onChange={(e) => handleFilterChange('make', e.target.value)}
                />
              </FilterGroup>

              <FilterGroup>
                <FilterLabel>Model</FilterLabel>
                <FilterInput
                  type="text"
                  placeholder="e.g., Camry, F-150, X5"
                  value={filters.model}
                  onChange={(e) => handleFilterChange('model', e.target.value)}
                />
              </FilterGroup>

              <FilterGroup>
                <FilterLabel>Year</FilterLabel>
                <FilterInput
                  type="number"
                  placeholder="e.g., 2020"
                  value={filters.year}
                  onChange={(e) => handleFilterChange('year', e.target.value)}
                />
              </FilterGroup>

              <FilterGroup>
                <FilterLabel>Min Price</FilterLabel>
                <FilterInput
                  type="number"
                  placeholder="e.g., 10000"
                  value={filters.minPrice}
                  onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                />
              </FilterGroup>

              <FilterGroup>
                <FilterLabel>Max Price</FilterLabel>
                <FilterInput
                  type="number"
                  placeholder="e.g., 50000"
                  value={filters.maxPrice}
                  onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                />
              </FilterGroup>

              <FilterGroup>
                <FilterLabel>Condition</FilterLabel>
                <FilterSelect
                  value={filters.newUsed}
                  onChange={(e) => handleFilterChange('newUsed', e.target.value)}
                >
                  <option value="">All Conditions</option>
                  <option value="new">New Vehicles</option>
                  <option value="used">Used Vehicles</option>
                </FilterSelect>
              </FilterGroup>

              <FilterGroup>
                <FilterLabel>Transmission</FilterLabel>
                <FilterSelect
                  value={filters.transmission}
                  onChange={(e) => handleFilterChange('transmission', e.target.value)}
                >
                  <option value="">All Transmissions</option>
                  <option value="automatic">Automatic</option>
                  <option value="manual">Manual</option>
                </FilterSelect>
              </FilterGroup>

              <FilterGroup>
                <FilterLabel>Drivetrain</FilterLabel>
                <FilterSelect
                  value={filters.drivetrain}
                  onChange={(e) => handleFilterChange('drivetrain', e.target.value)}
                >
                  <option value="">All Drivetrains</option>
                  <option value="Front-Wheel Drive">Front-Wheel Drive</option>
                  <option value="Rear-Wheel Drive">Rear-Wheel Drive</option>
                  <option value="Four-Wheel Drive">All-Wheel Drive</option>
                  <option value="Four-Wheel Drive">4-Wheel Drive</option>
                </FilterSelect>
              </FilterGroup>
            </FiltersGrid>

            {/* Active Filters Display */}
            {Object.values(filters).some(value => value !== '') && (
              <FilterChips>
                {Object.entries(filters).map(([key, value]) => 
                  value && (
                    <FilterChip key={key}>
                      {key === 'newUsed' ? 'Condition' : 
                       key === 'minPrice' ? 'Min Price' :
                       key === 'maxPrice' ? 'Max Price' :
                       key.charAt(0).toUpperCase() + key.slice(1)}: {value}
                      <span 
                        className="remove" 
                        onClick={() => handleFilterChange(key, '')}
                      >
                        <X size={14} />
                      </span>
                    </FilterChip>
                  )
                )}
              </FilterChips>
            )}

            <FiltersActions>
              <SearchButton onClick={applyFilters}>
                <Search size={20} />
                Search Inventory
              </SearchButton>
            </FiltersActions>
          </FiltersContent>
        </FiltersSection>

        <ResultsSection>
          <ResultsHeader>
            <h3>Available Vehicles</h3>
            <div className="results-count">
              {totalCars} vehicle{totalCars !== 1 ? 's' : ''} found
            </div>
          </ResultsHeader>

          {cars.length === 0 ? (
            <NoResultsContainer>
              <h3>No vehicles found</h3>
              <p>Try adjusting your filters or check back later</p>
            </NoResultsContainer>
          ) : (
            <>
              <CarsGrid>
                <AnimatePresence>
                  {cars.map((car, index) => (
                                         <CarCard
                       key={car.id}
                       initial={{ opacity: 0, y: 20 }}
                       animate={{ opacity: 1, y: 0 }}
                       exit={{ opacity: 0, y: -20 }}
                       transition={{ duration: 0.3, delay: index * 0.1 }}
                       onClick={() => handleCarClick(car)}
                     >
                                             <CarImage>
                         <img
                           src={(() => {
                             try {
                               if (car.imagelist) {
                                 // Handle both JSON and CSV formats
                                 let images;
                                 if (typeof car.imagelist === 'string') {
                                   // Try parsing as JSON first
                                   try {
                                     images = JSON.parse(car.imagelist);
                                   } catch {
                                     // If JSON parse fails, treat as CSV
                                     images = car.imagelist.split(',').map(url => ({ url: url.trim() }));
                                   }
                                 } else {
                                   images = car.imagelist;
                                 }
                                 return images[0]?.url || '/placeholder-car.jpg';
                               }
                               return '/placeholder-car.jpg';
                             } catch (e) {
                               return '/placeholder-car.jpg';
                             }
                           })()}
                           alt={`${car.make} ${car.model}`}
                           onError={(e) => {
                             e.target.src = '/placeholder-car.jpg';
                           }}
                         />
                       </CarImage>
                      <CarInfo>
                        <CarTitle>
                          {car.year} {car.make} {car.model}
                        </CarTitle>
                                                 <CarDetails>
                           <span>{car.trim}</span>
                           <span>{car.mileage ? `${car.mileage.toLocaleString()} miles` : 'N/A'}</span>
                           <span>{car.transmission}</span>
                           <span>{car.drivetrain}</span>
                         </CarDetails>
                                                 <CarPrice>
                           ${car.price && car.price !== '0' ? car.price.toLocaleString() : 'Call for Price'}
                         </CarPrice>
                                                 <CarActions>
                           <ActionButton onClick={(e) => {
                             e.stopPropagation();
                             navigate(`/car-details?id=${car.id}`);
                           }}>
                             View Details
                           </ActionButton>
                           <ActionButton 
                             primary
                             onClick={(e) => {
                               e.stopPropagation();
                               handleChatAboutCar(car);
                             }}
                           >
                             Chat about this
                           </ActionButton>
                         </CarActions>
                      </CarInfo>
                    </CarCard>
                  ))}
                </AnimatePresence>
              </CarsGrid>

                             {totalPages > 1 && (
                 <Pagination>
                   <PageButton
                     onClick={() => handlePageChange(currentPage - 1)}
                     disabled={currentPage === 1}
                   >
                     <ChevronLeft size={16} />
                     Previous
                   </PageButton>

                   {/* First page */}
                   {currentPage > 3 && (
                     <>
                       <PageButton
                         onClick={() => handlePageChange(1)}
                         active={false}
                       >
                         1
                       </PageButton>
                       {currentPage > 4 && <PageEllipsis>...</PageEllipsis>}
                     </>
                   )}

                   {/* Pages around current page */}
                   {Array.from(
                     { length: Math.min(5, totalPages) },
                     (_, i) => {
                       const startPage = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
                       const page = startPage + i;
                       if (page > totalPages) return null;
                       return (
                         <PageButton
                           key={page}
                           active={page === currentPage}
                           onClick={() => handlePageChange(page)}
                         >
                           {page}
                         </PageButton>
                       );
                     }
                   ).filter(Boolean)}

                   {/* Last page */}
                   {currentPage < totalPages - 2 && (
                     <>
                       {currentPage < totalPages - 3 && <PageEllipsis>...</PageEllipsis>}
                       <PageButton
                         onClick={() => handlePageChange(totalPages)}
                         active={false}
                       >
                         {totalPages}
                       </PageButton>
                     </>
                   )}

                   <PageButton
                     onClick={() => handlePageChange(currentPage + 1)}
                     disabled={currentPage === totalPages}
                   >
                     Next
                     <ChevronRight size={16} />
                   </PageButton>
                   
                   {/* Page jump input */}
                   {totalPages > 10 && (
                     <PageJumpContainer>
                       <span>Go to:</span>
                       <PageJumpInput
                         type="number"
                         placeholder="Page"
                         value={pageJumpInput}
                         onChange={(e) => setPageJumpInput(e.target.value)}
                         onKeyPress={handlePageJumpKeyPress}
                         min="1"
                         max={totalPages}
                       />
                       <PageJumpButton onClick={handlePageJump}>
                         Go
                       </PageJumpButton>
                     </PageJumpContainer>
                   )}
                 </Pagination>
               )}
            </>
          )}
        </ResultsSection>
      </MainContent>
    </Container>
  );
};

export default DealerInventory;
